import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { AlertTriangle } from 'lucide-react';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import DeletionImpactPreview, { type ImpactItem } from './DeletionImpactPreview';
import { handleApiError } from '../../services/api';

/**
 * Confirmation modal for irreversible, destructive actions (close workspace,
 * delete account). Requires the user to type an exact confirmation phrase, and —
 * unless they're OAuth-only — re-enter their password. `onConfirm` performs the
 * action; on success the caller handles the side effects (redirect / logout) and
 * the modal closes itself. Presents the action as permanent (no mention of grace).
 *
 * The typed-confirmation phrase is pinned to the field's top border (GCP-style) so
 * the exact spelling stays visible while typing — never a disappearing placeholder.
 */
const DangerConfirmModal: React.FC<{
  title: string;
  /** Consequences copy shown above the inputs. */
  intro: React.ReactNode;
  /** Exact phrase the user must type to enable the confirm button. */
  confirmWord: string;
  /** Optional "what gets deleted" counts, shown between the intro and the inputs. */
  impact?: ImpactItem[];
  confirmButtonText: string;
  requirePassword: boolean;
  onConfirm: (password: string) => Promise<void>;
  onClose: () => void;
}> = ({ title, intro, confirmWord, impact, confirmButtonText, requirePassword, onConfirm, onClose }) => {
  const [password, setPassword] = useState('');
  const [typed, setTyped] = useState('');

  const mutation = useMutation({
    mutationFn: () => onConfirm(password),
    onSuccess: onClose,
  });

  const typedMatches = typed.trim() === confirmWord;
  const canSubmit = typedMatches && (!requirePassword || password.length > 0);

  return (
    <Modal isOpen onClose={onClose} size="sm">
      <ModalHeader onClose={onClose}>{title}</ModalHeader>
      <ModalBody>
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg bg-destructive/10 border border-destructive/20 p-3">
            <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
            <div className="text-sm text-foreground">{intro}</div>
          </div>

          {impact && impact.length > 0 && <DeletionImpactPreview items={impact} />}

          {requirePassword && (
            <Input
              label="Your password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
              autoFocus
            />
          )}

          {/* Confirm field: the exact phrase to type straddles the top border and
              stays selectable, so spelling is always visible (GCP delete-dialog pattern). */}
          <div className="relative">
            <label
              htmlFor="danger-confirm"
              title={confirmWord}
              className="absolute -top-2 left-2.5 z-10 max-w-[calc(100%-1.25rem)] truncate bg-card px-1 text-[11px] font-medium text-muted-foreground"
            >
              To confirm, type{' '}
              <span className="select-all font-mono font-semibold text-foreground">{confirmWord}</span>
            </label>
            <input
              id="danger-confirm"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={confirmWord}
              autoComplete="off"
              autoFocus={!requirePassword}
              className={clsx(
                'block w-full rounded-lg border bg-card px-3 pb-2 pt-3 text-sm text-foreground',
                'placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring',
                typedMatches
                  ? 'border-success focus:border-success'
                  : 'border-input focus:border-primary',
              )}
            />
          </div>

          {mutation.isError && (
            <p className="text-sm text-destructive">{handleApiError(mutation.error)}</p>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={() => mutation.mutate()}
          isLoading={mutation.isPending}
          disabled={!canSubmit}
        >
          {confirmButtonText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DangerConfirmModal;
